import type { ReactNode } from 'react';

import type { WorkflowDefinition } from '../shared/index.js';

import type { Comment, DirectoryUser, TicketEvent } from '../api/endpoints.js';
import { Badge } from './ui.js';
import { personLabel } from './ticket-card.js';

interface TicketHistoryProps {
  readonly events: readonly TicketEvent[];
  readonly comments: readonly Comment[];
  readonly workflow: WorkflowDefinition;
  readonly people: ReadonlyMap<string, DirectoryUser>;
}

const FIELD_LABELS: Readonly<Record<string, string>> = Object.freeze({
  assigneeId: 'assignee',
  costCenter: 'cost centre',
  currency: 'currency',
  deadline: 'deadline',
  deliveryLocation: 'delivery location',
  departmentId: 'department',
  description: 'description',
  employmentType: 'employment type',
  estimatedAmount: 'estimated amount',
  language: 'language',
  neededBy: 'needed by',
  numberOfPeople: 'number of people',
  position: 'position',
  priority: 'priority',
  projectId: 'project',
  projectNote: 'project note',
  title: 'title',
});

function fieldLabel(name: string): string {
  return FIELD_LABELS[name] ?? name.replace(/([A-Z])/g, ' $1').toLowerCase();
}

function stageLabel(workflow: WorkflowDefinition, slug: string | null): string {
  if (slug === null) return 'somewhere';
  return workflow.stages.find((stage) => stage.slug === slug)?.label ?? slug;
}

function actorName(event: TicketEvent): string {
  if (event.actor === null) return 'Someone since removed';

  const name = `${event.actor.firstName} ${event.actor.lastName}`.trim();

  return name === '' ? 'Someone' : name;
}

function sentence(
  event: TicketEvent,
  workflow: WorkflowDefinition,
  people: ReadonlyMap<string, DirectoryUser>,
): ReactNode {
  const who = actorName(event);

  switch (event.type) {
    case 'ticket.created':
      return (
        <>
          <strong>{who}</strong> raised the request in{' '}
          <strong>{stageLabel(workflow, event.toValue)}</strong>
        </>
      );

    case 'ticket.stage_changed':
      return (
        <>
          <strong>{who}</strong> moved it from{' '}
          <strong>{stageLabel(workflow, event.fromValue)}</strong> to{' '}
          <strong>{stageLabel(workflow, event.toValue)}</strong>
        </>
      );

    case 'ticket.assigned':
      if (event.toValue === null) {
        return (
          <>
            <strong>{who}</strong> left it unassigned
          </>
        );
      }

      return (
        <>
          <strong>{who}</strong> assigned it to{' '}
          <strong>{personLabel(people, event.toValue)}</strong>
          {event.fromValue === null ? null : <> (was {personLabel(people, event.fromValue)})</>}
        </>
      );

    case 'ticket.updated': {
      const fields = (event.detail ?? '')
        .split(',')
        .map((name) => name.trim())
        .filter((name) => name !== '' && name !== 'assigneeId' && name !== 'updatedAt')
        .map(fieldLabel);

      if (fields.length === 0) return null;

      return (
        <>
          <strong>{who}</strong> changed the <strong>{fields.join(', ')}</strong>
        </>
      );
    }

    case 'ticket.status_changed':
      return (
        <>
          <strong>{who}</strong> set the status to <strong>{event.toValue ?? 'unknown'}</strong>
        </>
      );

    case 'ticket.archived':
      return (
        <>
          <strong>{who}</strong> archived it
        </>
      );

    case 'ticket.unarchived':
      return (
        <>
          <strong>{who}</strong> restored it to the board
        </>
      );

    default:
      return null;
  }
}

interface Entry {
  readonly id: string;
  readonly at: number;
  readonly body: ReactNode;
  readonly note: string | null;
  readonly internal: boolean;
}

export function TicketHistory({
  events,
  comments,
  workflow,
  people,
}: TicketHistoryProps): ReactNode {
  const entries: Entry[] = [];

  for (const event of events) {
    const body = sentence(event, workflow, people);

    if (body === null) continue;

    entries.push({
      id: event.id,
      at: new Date(event.createdAt).getTime(),
      body,
      note: event.type === 'ticket.stage_changed' ? event.detail : null,
      internal: false,
    });
  }

  for (const comment of comments) {
    const name = `${comment.author.firstName} ${comment.author.lastName}`.trim();

    entries.push({
      id: `comment-${comment.id}`,
      at: new Date(comment.createdAt).getTime(),
      body: (
        <>
          <strong>{name === '' ? 'Someone' : name}</strong> commented
        </>
      ),
      note: comment.body,
      internal: comment.isInternal,
    });
  }

  entries.sort((left, right) => left.at - right.at);

  if (entries.length === 0) {
    return <p className="text-[13.5px] text-muted">Nothing has happened yet.</p>;
  }

  return (
    <ol className="flex flex-col">
      {entries.map((entry, index) => (
        <li key={entry.id} className="flex gap-3">
          <div className="flex w-3 shrink-0 flex-col items-center pt-1.5">
            <span
              className={`size-2 shrink-0 rounded-full ${
                entry.internal ? 'bg-caution' : 'bg-royal/45'
              }`}
            />
            {index === entries.length - 1 ? null : (
              <span className="w-px flex-1 bg-hairline dark:bg-dark-line" />
            )}
          </div>

          <div className="min-w-0 flex-1 pb-4">
            <p className="text-[13px] leading-relaxed text-navy/85 dark:text-dark-ink/85">
              {entry.body}
              {entry.internal ? (
                <>
                  {' '}
                  <Badge tone="caution">Internal</Badge>
                </>
              ) : null}
            </p>

            <p className="mt-0.5 text-[11.5px] text-muted">{new Date(entry.at).toLocaleString()}</p>

            {entry.note === null || entry.note.trim() === '' ? null : (
              <p className="mt-1.5 rounded-panel bg-mist/70 px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap text-navy/80 dark:bg-white/5 dark:text-dark-ink/80">
                {entry.note}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
